import React, { useState } from "react";
import { toast } from "sonner";
import { Globe, PencilLine, Loader2, Sparkles, Save, X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const INPUT_CLS = "mt-1.5 bg-black border-white/15 rounded-xl focus-visible:ring-purple-500";

const ModeSelector = ({ mode, setMode }) => (
  <div className="grid grid-cols-2 gap-3">
    <button
      type="button"
      data-testid="mode-auto"
      onClick={() => setMode("auto")}
      className={`rounded-xl border p-4 text-left transition-all ${mode === "auto" ? "border-purple-500 bg-purple-500/10" : "border-white/10 bg-black hover:border-purple-500/50"}`}
    >
      <Globe className="text-purple-400 mb-1.5" size={20} />
      <div className="font-semibold text-sm">Autollenado</div>
      <div className="text-xs text-zinc-500">La IA extrae los datos desde la web de la empresa.</div>
    </button>
    <button
      type="button"
      data-testid="mode-manual"
      onClick={() => setMode("manual")}
      className={`rounded-xl border p-4 text-left transition-all ${mode === "manual" ? "border-purple-500 bg-purple-500/10" : "border-white/10 bg-black hover:border-purple-500/50"}`}
    >
      <PencilLine className="text-yellow-400 mb-1.5" size={20} />
      <div className="font-semibold text-sm">Manual</div>
      <div className="text-xs text-zinc-500">Escribe tú mismo los datos de la empresa.</div>
    </button>
  </div>
);

const AutoExtract = ({ url, setUrl, extracting, extract }) => (
  <div>
    <Label className="text-zinc-300">URL de la empresa</Label>
    <div className="flex gap-2 mt-1.5">
      <Input
        data-testid="company-url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://www.empresa.com"
        className="bg-black border-white/15 rounded-xl py-6 focus-visible:ring-purple-500"
      />
      <Button data-testid="extract-company" type="button" onClick={extract} disabled={extracting} className="bg-purple-600 hover:bg-purple-500 rounded-xl px-5">
        {extracting ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} className="mr-2" />}
        {extracting ? "Extrayendo..." : "Extraer con IA"}
      </Button>
    </div>
    <p className="text-xs text-zinc-500 mt-1.5">La IA leerá la página y extraerá nombre, productos/servicios y plan de ganancias (si aparece).</p>
  </div>
);

const ManualFields = ({ form, setField, isEdit, saving, save, onCancel }) => (
  <div className="space-y-4">
    <div>
      <Label className="text-zinc-300">Nombre de la empresa</Label>
      <Input data-testid="company-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Ej. Vida Plena Global" className={`${INPUT_CLS} py-6`} />
    </div>
    <div>
      <Label className="text-zinc-300">Productos / Servicios</Label>
      <Textarea data-testid="company-products" value={form.products_services} onChange={(e) => setField("products_services", e.target.value)} placeholder="¿Qué vende la empresa? Beneficios y a quién va dirigido..." rows={4} className={INPUT_CLS} />
    </div>
    <div>
      <Label className="text-zinc-300">Plan de ganancias / Comisiones</Label>
      <Textarea data-testid="company-plan" value={form.compensation_plan} onChange={(e) => setField("compensation_plan", e.target.value)} placeholder="Bonos, niveles, comisiones... (opcional)" rows={3} className={INPUT_CLS} />
    </div>
    <div className="flex gap-2">
      <Button data-testid="save-company" type="button" onClick={save} disabled={saving} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-6 py-6">
        <Save size={18} className="mr-2" /> {saving ? "Guardando..." : (isEdit ? "Guardar cambios" : "Guardar empresa")}
      </Button>
      {onCancel && (
        <Button type="button" variant="outline" onClick={onCancel} className="border-white/15 text-zinc-300 rounded-xl py-6">
          <X size={18} className="mr-1" /> Cancelar
        </Button>
      )}
    </div>
  </div>
);

// Formulario para dar de alta o editar una EMPRESA (AUTOLLENADO o MANUAL)
export const CompanyForm = ({ initial = null, onSaved, onCancel }) => {
  const isEdit = !!initial;
  const [mode, setMode] = useState("manual"); // "auto" | "manual"
  const [url, setUrl] = useState(initial?.source_url || "");
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: initial?.name || "",
    products_services: initial?.products_services || "",
    compensation_plan: initial?.compensation_plan || "",
    source_url: initial?.source_url || "",
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const extract = async () => {
    if (!url.trim()) return toast.error("Escribe la dirección web (URL) de la empresa.");
    setExtracting(true);
    try {
      const { data } = await api.post("/companies/extract", { url: url.trim() });
      setForm({
        name: data.name || "",
        products_services: data.products_services || "",
        compensation_plan: data.compensation_plan || "",
        source_url: data.source_url || url.trim(),
      });
      setMode("manual"); // pasa a revisión de los datos extraídos
      toast.success("Datos extraídos. Revísalos y guarda.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setExtracting(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("La empresa necesita al menos un nombre.");
    setSaving(true);
    try {
      const { data } = isEdit
        ? await api.put(`/companies/${initial.company_id}`, form)
        : await api.post("/companies", form);
      toast.success(isEdit ? "Empresa actualizada" : "Empresa guardada");
      onSaved?.(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="company-form" className="space-y-5">
      {!isEdit && <ModeSelector mode={mode} setMode={setMode} />}
      {mode === "auto" && !isEdit && <AutoExtract url={url} setUrl={setUrl} extracting={extracting} extract={extract} />}
      {(mode === "manual" || isEdit) && (
        <ManualFields form={form} setField={setField} isEdit={isEdit} saving={saving} save={save} onCancel={onCancel} />
      )}
    </div>
  );
};
